import React, { useEffect, useRef, useState } from 'react'

import Icon from '../components/icon'
import { waLink } from '../site'

// Logo de un conector: su ícono sobre un fondo con su color. No usa los logos de las marcas.
export const ConnectorLogo = ({ connector, size = 48 }) => (
  <span
    className="sec-logo"
    style={{ width: size, height: size, color: connector.color, background: `${connector.color}17` }}
    aria-hidden="true"
  >
    <Icon name={connector.icon} size={Math.round(size * 0.5)} />
  </span>
)

export const StatusBadge = ({ status }) =>
  status === 'available' ? (
    <span className="sec-status sec-status--on">Disponible</span>
  ) : (
    <span className="sec-status sec-status--planned">En la hoja de ruta</span>
  )

// Copia con execCommand: respaldo para navegadores sin la API del portapapeles o que la bloquean.
const legacyCopy = (text) => {
  const area = document.createElement('textarea')
  area.value = text
  area.setAttribute('readonly', '')
  area.style.position = 'fixed'
  area.style.opacity = '0'
  document.body.appendChild(area)
  area.select()
  const ok = document.execCommand('copy')
  document.body.removeChild(area)
  if (!ok) throw new Error('copy failed')
}

const copyText = async (text) => {
  if (navigator.clipboard && window.isSecureContext) {
    try {
      await navigator.clipboard.writeText(text)
      return
    } catch (e) {
      // Sigue con el respaldo.
    }
  }
  legacyCopy(text)
}

// Una instrucción de ejemplo: se copia, o se abre en WhatsApp ya escrita para enviársela a Secretario.
export const PromptExample = ({ text }) => {
  const [copied, setCopied] = useState(false)
  const timer = useRef(null)

  useEffect(() => () => clearTimeout(timer.current), [])

  const onCopy = async () => {
    try {
      await copyText(text)
      setCopied(true)
      clearTimeout(timer.current)
      timer.current = setTimeout(() => setCopied(false), 1800)
    } catch (e) {
      setCopied(false)
    }
  }

  return (
    <li className="sec-prompt">
      <span className="sec-prompt__text">«{text}»</span>
      <span className="sec-prompt__actions">
        <button type="button" className="sec-icon-btn" onClick={onCopy} aria-label={`Copiar: ${text}`}>
          <Icon name={copied ? 'check' : 'copy'} size={16} />
          <span>{copied ? 'Copiado' : 'Copiar'}</span>
        </button>
        <a
          className="sec-icon-btn"
          href={waLink(text)}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={`Probar en WhatsApp: ${text}`}
        >
          <Icon name="send" size={16} />
          <span>Probar</span>
        </a>
      </span>
      <span className="sec-visually-hidden" aria-live="polite">
        {copied ? 'Instrucción copiada' : ''}
      </span>
    </li>
  )
}

// Video tutorial. Muestra la miniatura y carga el reproductor solo al pulsar «reproducir».
// Con `src` usa el reproductor del navegador (controles y pantalla completa); con `embed`, un iframe.
// Sin video, muestra el estado «en preparación» con el mismo tamaño, para no mover el diseño.
export const VideoPlayer = ({ video, fallbackTitle }) => {
  const [playing, setPlaying] = useState(false)
  const ready = video && (video.src || video.embed)

  if (!ready) {
    return (
      <figure className="sec-video">
        <div className="sec-video__frame sec-video__frame--empty">
          <span className="sec-video__soon">
            <Icon name="play" size={22} />
          </span>
          <strong>Video tutorial en preparación</strong>
          <span>Mientras tanto, sigue la guía paso a paso de esta página.</span>
        </div>
        <figcaption className="sec-video__caption">
          <strong>{fallbackTitle}</strong>
          <span className="sec-video__duration">Próximamente</span>
        </figcaption>
      </figure>
    )
  }

  return (
    <figure className="sec-video">
      <div className="sec-video__frame">
        {playing && video.src && (
          <video src={video.src} poster={video.poster} controls autoPlay playsInline preload="metadata" />
        )}
        {playing && !video.src && (
          <iframe
            src={video.embed}
            title={video.title}
            allow="autoplay; fullscreen; picture-in-picture; encrypted-media"
            allowFullScreen
            loading="lazy"
          />
        )}
        {!playing && (
          <button
            type="button"
            className="sec-video__poster"
            onClick={() => setPlaying(true)}
            style={video.poster ? { backgroundImage: `url(${video.poster})` } : undefined}
            aria-label={`Reproducir: ${video.title}${video.duration ? `, ${video.duration}` : ''}`}
          >
            <span className="sec-video__play">
              <Icon name="play" size={26} />
            </span>
            {video.duration && <span className="sec-video__time">{video.duration}</span>}
          </button>
        )}
      </div>
      <figcaption className="sec-video__caption">
        <strong>{video.title}</strong>
        {video.duration && <span className="sec-video__duration">{video.duration} min</span>}
        {video.description && <p>{video.description}</p>}
      </figcaption>
    </figure>
  )
}
